import React from 'react';
import { FiSend, FiShoppingCart, FiArchive, FiTarget, FiTool, FiCpu, FiHome, FiBriefcase, FiZap } from 'react-icons/fi';
import { StationView } from './StationUI';
import { PALETTE } from './stationTheme';

interface StationLobbyProps {
  stationId: string;
  stationName: string;
  onNavigate: (view: StationView) => void;
  isPrivate?: boolean;
  isSettlement?: boolean;
}

interface ZoneConfig {
  id: StationView;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const zones: ZoneConfig[] = [
  {
    id: 'shipyard',
    label: 'Shipyard',
    icon: <FiSend size={16} />,
    description: 'Manage your ships',
  },
  {
    id: 'market',
    label: 'Marketplace',
    icon: <FiShoppingCart size={16} />,
    description: 'Trade resources and equipment',
  },
  {
    id: 'bank',
    label: 'Bank',
    icon: <FiArchive size={16} />,
    description: 'Store credits and items safely',
  },
  {
    id: 'bounties',
    label: 'Bounties',
    icon: <FiTarget size={16} />,
    description: 'Accept missions and contracts',
  },
  {
    id: 'repair',
    label: 'Repair Bay',
    icon: <FiTool size={16} />,
    description: 'Repair your ship hull and systems',
  },
  {
    id: 'dronebay',
    label: 'Drone Bay',
    icon: <FiCpu size={16} />,
    description: 'Deploy and manage station drones',
  },
  {
    id: 'crafting',
    label: 'Crafting Station',
    icon: <FiZap size={16} />,
    description: 'Refine ore and craft equipment',
  },
  {
    id: 'corporation',
    label: 'Corporation Office',
    icon: <FiBriefcase size={16} />,
    description: 'Found or manage a corporation',
  },
  {
    id: 'private_station',
    label: 'Private Station',
    icon: <FiHome size={16} />,
    description: 'Buy or manage your private outpost in this constellation',
  },
  {
    id: 'private_bank',
    label: 'Station Bank (local)',
    icon: <FiArchive size={16} />,
    description: 'Resources stored here only — accessible while docked',
  },
];

export function StationLobby({ stationId, stationName, onNavigate, isPrivate, isSettlement }: StationLobbyProps) {
  // Settlements: a tradeable rogue-drone nest — order book ONLY (no shipyard/bank/repair/etc).
  // Private stations: owner-only; show drone bay, shipyard, repair, private panel.
  // NPC stations: hide the local-only `private_bank` view entirely.
  const visibleZones = (isSettlement
    ? zones.filter(z => z.id === 'market')
    : isPrivate
    ? zones.filter(z => z.id === 'shipyard' || z.id === 'repair' || z.id === 'dronebay' || z.id === 'private_station' || z.id === 'private_bank')
    : zones.filter(z => z.id !== 'private_bank' && z.id !== 'dronebay')
  ).map(z => {
    if (isPrivate && z.id === 'private_station') {
      return { ...z, label: 'Manage Station', description: 'Manage your private outpost' };
    }
    if (isPrivate && z.id === 'private_bank') {
      return { ...z, label: 'Station Bank', description: 'Resources stored here only' };
    }
    return z;
  });
  void stationId; void stationName;

  return (
    <div style={wrapperStyle}>
      {/* Section title */}
      <div style={sectionTitleStyle}>{isSettlement ? 'ROGUE SETTLEMENT' : 'STATION SERVICES'}</div>

      {/* Service buttons — vertical list for the side panel */}
      <div style={listStyle}>
        {visibleZones.map((zone) => (
          <button
            key={zone.id}
            style={itemStyle}
            onClick={() => onNavigate(zone.id)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = PALETTE.BORDER;
              e.currentTarget.style.borderColor = PALETTE.TEAL;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = PALETTE.PANEL;
              e.currentTarget.style.borderColor = PALETTE.BORDER;
            }}
          >
            <span style={iconBoxStyle}>
              {React.cloneElement(zone.icon as React.ReactElement<{ size: number }>, { size: 18 })}
            </span>
            <div style={textGroupStyle}>
              <div style={labelStyle}>{zone.label}</div>
              <div style={descStyle}>{zone.description}</div>
            </div>
          </button>
        ))}
      </div>

    </div>
  );
}

export default StationLobby;

// ==================== STYLES ====================

const wrapperStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  padding: '16px',
  gap: '12px',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.TEAL,
  letterSpacing: '0.15em',
  fontWeight: 600,
  padding: '0 4px',
};

const listStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  flex: 1,
  overflow: 'auto',
};

const itemStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  backgroundColor: PALETTE.PANEL,
  border: `1px solid ${PALETTE.BORDER}`,
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.15s, border-color 0.15s',
  textAlign: 'left',
};

const iconBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  backgroundColor: PALETTE.DARK,
  borderRadius: '6px',
  color: PALETTE.TEAL,
  flexShrink: 0,
};

const textGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  minWidth: 0,
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: PALETTE.LIGHT,
};

const descStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.DIM,
  lineHeight: '1.3',
};

